import { memo, useId } from "react";

const CX = 410, CY = 400;

export function ReactorMark({ size = 156 }) {
  const id = `reactor-mark-${useId().replace(/:/g, "")}`;
  const theme = { ring: "#18c9ff", glow: "#82eeff" };
  return <svg width={size} height={size} viewBox="188 178 444 444" role="img" aria-label="Jarvis triangular arc reactor" data-testid="lock-reactor-mark"
    style={{ "--reactor-ring": theme.ring, "--reactor-glow": theme.glow }}>
    <ReactorDefs id={id} theme={theme} />
    <circle cx="410" cy="400" r="218" fill="none" stroke="#18c9ff" strokeWidth="1.5" opacity=".55" />
    <circle cx="410" cy="400" r="210" fill="none" stroke="#83dfff" strokeWidth="5" strokeDasharray="2 3" opacity=".65" />
    <ReactorEngine id={id} open={false} />
  </svg>;
}

export const CORE_NAV = {
  top: ["Workspaces", "Agents", "Commands", "Tasks", "Automations"],
  left: ["Settings", "Terminal", "Revenue", "Activity", "AI Usage", "Models"],
  right: ["Devices", "Servers", "Plugins", "Skills", "Tools", "Files"],
  bottom: ["Memory", "Vault", "Security"],
};
export const navPath = (label) => `/${label.toLowerCase().replace(/\s/g, "-")}`;
export function point(r, deg) {
  const a = deg * Math.PI / 180;
  return [CX + r * Math.sin(a), CY - r * Math.cos(a)];
}
function arc(r, start, end) {
  return `M ${point(r, start)} A ${r} ${r} 0 ${Math.abs(end - start) > 180 ? 1 : 0} ${end > start ? 1 : 0} ${point(r, end)}`;
}
function band(inner, outer, start, end) {
  return `${arc(outer, start, end)} L ${point(inner, end)} A ${inner} ${inner} 0 0 ${end > start ? 0 : 1} ${point(inner, start)} Z`;
}
function RadialLine({ r1, r2, angle, ...props }) {
  const [x1, y1] = point(r1, angle), [x2, y2] = point(r2, angle);
  return <line x1={x1} y1={y1} x2={x2} y2={y2} {...props} />;
}

export function ReactorDefs({ id, theme }) {
  return <defs>
    <radialGradient id={`${id}-halo`}><stop stopColor={theme.ring} stopOpacity=".48" /><stop offset=".7" stopColor={theme.ring} stopOpacity=".13" /><stop offset="1" stopColor={theme.ring} stopOpacity="0" /></radialGradient>
    <radialGradient id={`${id}-panel`} cx="50%" cy="55%" r="65%">
      <stop stopColor="#fff" /><stop offset=".48" stopColor="#f3ffff" /><stop offset=".72" stopColor="#a4f1ff" /><stop offset=".9" stopColor="#27718c" /><stop offset="1" stopColor="#071722" />
    </radialGradient>
    <linearGradient id={`${id}-metal`} x1="0" y1="0" x2="1" y2="1">
      <stop stopColor="#c5e2eb" /><stop offset=".12" stopColor="#1c3747" /><stop offset=".36" stopColor="#040b12" /><stop offset=".52" stopColor="#4b7488" /><stop offset=".62" stopColor="#07131d" /><stop offset=".87" stopColor="#01060a" /><stop offset="1" stopColor="#a2d0e2" />
    </linearGradient>
    <linearGradient id={`${id}-silver`} x1="0" y1="0" x2=".8" y2="1">
      <stop stopColor="#fff" /><stop offset=".18" stopColor="#94bfd3" /><stop offset=".33" stopColor="#efffff" /><stop offset=".47" stopColor="#45677f" /><stop offset=".6" stopColor="#f4ffff" /><stop offset=".81" stopColor="#5687a2" /><stop offset="1" stopColor="#e5ffff" />
    </linearGradient>
    <radialGradient id={`${id}-plasma`} cx="50%" cy="38%" r="69%">
      <stop stopColor="#edffff" /><stop offset=".13" stopColor={theme.glow} /><stop offset=".33" stopColor={theme.ring} /><stop offset=".57" stopColor="#0359b2" /><stop offset=".83" stopColor="#00142d" /><stop offset="1" stopColor="#020910" />
    </radialGradient>
    <linearGradient id={`${id}-channel`} x1="0" y1="0" x2="0" y2="1">
      <stop stopColor="#01265a" /><stop offset=".35" stopColor={theme.ring} /><stop offset=".62" stopColor="#97f8ff" /><stop offset="1" stopColor="#005cb9" stopOpacity="0" />
    </linearGradient>
    <radialGradient id={`${id}-bulb`}>
      <stop stopColor="#fff" /><stop offset=".15" stopColor="#fff" /><stop offset=".36" stopColor="#c6ffff" /><stop offset=".55" stopColor={theme.glow} stopOpacity=".94" /><stop offset="1" stopColor={theme.ring} stopOpacity="0" />
    </radialGradient>
    <filter id={`${id}-bloom`} x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="5" /><feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge></filter>
    <filter id={`${id}-soft`} x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="8" /></filter>
    <filter id={`${id}-energy`} x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="4" /></filter>
    <clipPath id={`${id}-triangle`}><path d="M259 313 L561 313 L410 561 Z" /></clipPath>
    <path id={`${id}-online-arc`} d={arc(221, -40, 40)} />
    <path id={`${id}-stop-arc`} d={arc(356, 165, 121)} />
  </defs>;
}

export const ReactorRings = memo(function ReactorRings({ id }) {
  return <g aria-hidden="true" pointerEvents="none">
    <circle cx={CX} cy={CY} r="385" fill={`url(#${id}-halo)`} />
    {[393, 385, 369, 320, 310, 277].map((r, i) => <circle key={r} cx={CX} cy={CY} r={r} fill="none" stroke={i % 2 ? "#a1d8ec" : "var(--reactor-ring)"} strokeOpacity={i % 2 ? ".4" : ".24"} strokeWidth={i === 4 ? "2" : ".8"} />)}
    {Array.from({ length: 32 }, (_, i) => <g key={i}>
      <path d={band(284, 307, i * 11.25 + 1, (i + 1) * 11.25 - 1)} fill="#05111b" stroke="#83b9cc" strokeOpacity=".46" strokeWidth=".6" />
      <RadialLine r1={285} r2={300} angle={i * 11.25 + 3} stroke="#c7eaff" strokeWidth=".7" opacity=".5" />
    </g>)}
    {[0, 30, 55, 125, 150, 210, 235, 305, 330].map((angle) => <g key={angle}>
      <path d={arc(377, angle - 4.5, angle + 4.5)} fill="none" stroke="#82d9ff" strokeWidth="7" opacity=".8" />
      <path d={arc(366, angle - 4, angle + 4)} fill="none" stroke="var(--reactor-ring)" strokeWidth="3" filter={`url(#${id}-bloom)`} />
      <RadialLine r1={278} r2={393} angle={angle} stroke="#acd8e6" opacity=".3" />
    </g>)}
    <g className="reactor-rotor reactor-rotor-outer">
      {Array.from({ length: 180 }, (_, i) => <RadialLine key={i} r1={i % 5 === 0 ? 237 : 247} r2={270} angle={i * 2} stroke="var(--reactor-ring)" strokeWidth={i % 5 === 0 ? "1" : ".5"} opacity={i % 5 === 0 ? ".65" : ".25"} />)}
      <circle cx={CX} cy={CY} r="262" fill="none" stroke="var(--reactor-ring)" strokeWidth="15" opacity=".21" />
      <circle cx={CX} cy={CY} r="258" fill="none" stroke="var(--reactor-ring)" strokeWidth="2.5" filter={`url(#${id}-bloom)`} />
      <circle cx={CX} cy={CY} r="250" fill="none" stroke="#8deaff" strokeWidth="1" strokeDasharray="8 3 2 3" />
      {[40, 80, 140, 220, 280, 320].map((a) => <path key={a} d={arc(265, a - 10, a + 9)} stroke="var(--reactor-ring)" strokeWidth="7" fill="none" opacity=".7" />)}
    </g>
    {[0, 38, 77, 103, 142, 180, 218, 257, 283, 322].map((a, i) => {
      const [x, y] = point(255, a);
      return <g key={a} transform={`translate(${x} ${y}) rotate(${a})`} filter={`url(#${id}-bloom)`}>
        {i % 3 === 1 ? <><circle r="6" fill="#052844" stroke="#a8faff" strokeWidth="2" /><circle r="3" fill="#fff" /></> : <path d="M0 6 L-5 -5 L5 -5 Z" fill="#dbffff" />}
      </g>;
    })}
    <circle cx={CX} cy={CY} r="217" fill="#030a10" stroke="var(--reactor-ring)" strokeWidth="1.4" />
    <g className="reactor-rotor reactor-rotor-teeth" data-testid="reactor-toothed-ring">
      <circle cx={CX} cy={CY} r="200" fill="none" stroke={`url(#${id}-silver)`} strokeWidth="15" />
      {Array.from({ length: 120 }, (_, i) => <g key={i} transform={`rotate(${i * 3} ${CX} ${CY})`}>
        <path d="M407 190 L408 203 L411 203 L412 190 Z" fill={i % 3 ? "#d8f6ff" : "#fff"} />
        <path d="M412 190 L413 192 L412 204 L410 203 Z" fill="#315871" />
      </g>)}
      <circle cx={CX} cy={CY} r="213" fill="none" stroke="#a5eaff" strokeWidth="4" strokeDasharray="1 3" />
      <circle cx={CX} cy={CY} r="194" fill="none" stroke="#f0ffff" strokeWidth="3" filter={`url(#${id}-bloom)`} />
    </g>
    <circle cx={CX} cy={CY} r="190" fill="#030c13" stroke="#719cad" strokeWidth="2" />
  </g>;
});

function Bolt({ x, y, id, small = false }) {
  return <g transform={`translate(${x} ${y}) scale(${small ? .48 : 1})`}>
    <path d="M-7 -4 L0 -8 L7 -4 L7 4 L0 8 L-7 4 Z" fill={`url(#${id}-metal)`} stroke="#92c5dc" strokeWidth=".8" />
    <circle r="4" fill="#01080e" stroke="#315976" strokeWidth="1.5" /><path d="M-2 1 L2 -1" stroke="#a3e4ff" />
  </g>;
}

export const ReactorEngine = memo(function ReactorEngine({ id, open }) {
  return <g aria-hidden="true" pointerEvents="none" data-testid="reactor-engine">
    <g className={`reactor-panels ${open ? "reactor-panels-open" : ""}`} data-testid="reactor-white-panels">
      {Array.from({ length: 6 }, (_, i) => <g key={i} transform={`rotate(${i * 60} ${CX} ${CY})`}>
        <g className="reactor-panel-slide">
          <path d="M330 232 L360 218 Q410 200 460 218 L490 232 L461 308 L359 308 Z" fill={`url(#${id}-metal)`} stroke="#6a9bad" strokeWidth="1.2" />
          <path d="M346 239 L366 226 Q410 213 454 226 L474 239 L451 295 L369 295 Z" fill={`url(#${id}-panel)`} stroke="#143d50" strokeWidth="4" />
          <path d="M368 231 Q410 217 453 231" stroke="#edffff" strokeWidth="2" fill="none" opacity=".7" />
          <path d="M345 242 L366 302 M475 242 L454 302" stroke="#060e16" strokeWidth="8" />
          <Bolt x={358} y={232} id={id} small /><Bolt x={462} y={232} id={id} small />
          <path d="M393 208 L389 217 L405 214 L411 207 Z" fill="#f4ffff" />
        </g>
      </g>)}
    </g>
    <g data-testid="reactor-triangle">
      <path d="M246 293 L574 293 Q585 295 577 310 L419 578 Q410 593 401 578 L243 310 Q236 299 246 293Z" fill="#00050a" stroke="#02080e" strokeWidth="12" />
      <path d="M249 300 L571 300 L410 578 Z" fill={`url(#${id}-metal)`} stroke="#789eb0" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M259 313 L561 313 L410 561 Z" fill={`url(#${id}-plasma)`} stroke="#020b12" strokeWidth="6" strokeLinejoin="round" />
      <g clipPath={`url(#${id}-triangle)`} className="reactor-plasma">
        {[0, 120, 240].map((a) => <g key={a} transform={`rotate(${a} 410 400)`} filter={`url(#${id}-energy)`}>
          <path d="M270 314 Q410 300 550 314 L490 356 Q410 377 330 356 Z" fill={`url(#${id}-channel)`} />
          {[0, 1, 2, 3, 4, 5, 6].map((n) => <path key={n} d={`M${292 + n * 32} 318 l8 0 -3 34 -9 -5 Z`} fill="#4ef3ff" opacity=".3" />)}
        </g>)}
        <path d="M285 330 L535 330 L410 541 Z" fill="none" stroke="#11acff" strokeWidth="3" opacity=".38" filter={`url(#${id}-bloom)`} />
      </g>
      <path d="M250 302 L568 302 M572 307 L412 579 M407 577 L248 308" fill="none" stroke={`url(#${id}-silver)`} strokeWidth="2" opacity=".6" />
      <path d="M263 308 L557 308 L410 566 Z" fill="none" stroke="#020a10" strokeWidth="5" strokeLinejoin="round" />
      <Bolt x={257} y={309} id={id} /><Bolt x={563} y={309} id={id} /><Bolt x={410} y={570} id={id} />
    </g>
    <g className="reactor-plasma" data-testid="reactor-central-light">
      <circle cx={CX} cy={CY} r="72" fill={`url(#${id}-bulb)`} filter={`url(#${id}-soft)`} />
      <circle cx={CX} cy={CY} r="47" fill="none" stroke="#abf2ff" strokeWidth=".8" />
      <circle cx={CX} cy={CY} r="39" fill="none" stroke="#b4ffff" strokeWidth="2" opacity=".9" />
      <circle cx={CX} cy={CY} r="31" fill="none" stroke="#fff" strokeWidth="1" />
      <circle cx={CX} cy={CY} r="43" fill="none" stroke="#ebffff" strokeWidth="2" strokeDasharray="18 12 3 10" className="reactor-rotor reactor-rotor-hub" />
      <path d="M352 400 H468 M410 342 V458" stroke="#a7f2ff" strokeWidth=".7" opacity=".65" />
      <circle cx={CX} cy={CY} r="37" fill={`url(#${id}-bulb)`} /><circle cx={CX} cy={CY} r="9" fill="#fff" filter={`url(#${id}-bloom)`} />
    </g>
  </g>;
});

function NavLink({ label, onNavigate, children }) {
  const path = navPath(label);
  return <a href={path} onClick={(e) => { if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) { e.preventDefault(); onNavigate?.(path); } }}
    data-testid={`ring-nav-${path.slice(1)}`} className="reactor-nav" aria-label={label}>
    <title>{label}</title>{children}
  </a>;
}

export function ReactorNavigation({ onNavigate }) {
  return <g className="reactor-labels" data-testid="reactor-navigation">
    {CORE_NAV.top.map((label, i) => {
      const angle = -40 + i * 20, [x, y] = point(348, angle);
      return <NavLink key={label} label={label} onNavigate={onNavigate}>
        <path className="reactor-nav-hit" d={band(274, 371, angle - 9.6, angle + 9.6)} />
        <path d={arc(277, angle - 9.3, angle + 9.3)} fill="none" stroke="var(--reactor-ring)" strokeWidth="3" opacity=".7" />
        <RadialLine r1={274} r2={367} angle={angle - 9.6} stroke="#b4dce9" opacity=".8" strokeWidth="1.1" />
        <text x={x} y={y} dy=".35em" textAnchor="middle" transform={`rotate(${angle} ${x} ${y})`} fontSize="17" fill="#96ecff">{label.toUpperCase()}</text>
        <path d={arc(316, angle - 9.6, angle + 9.6)} fill="none" stroke="#bde5ef" strokeWidth="1.5" />
      </NavLink>;
    })}
    {["left", "right"].map((side) => CORE_NAV[side].map((label, i) => {
      const sign = side === "left" ? -1 : 1;
      const angle = sign * (70 + i * 8.8);
      const [mx, my] = point(299, angle), [tx] = point(363, angle);
      return <NavLink key={label} label={label} onNavigate={onNavigate}>
        <path className="reactor-nav-hit" d={band(271, 391, angle - 4.4, angle + 4.4)} />
        <path d={arc(274, angle - 4.1, angle + 4.1)} fill="none" stroke="var(--reactor-ring)" opacity=".8" strokeWidth="2.5" />
        <path d={arc(390, angle - 4.4, angle + 4.4)} fill="none" stroke="#b5d7e5" strokeWidth="1.4" opacity=".8" />
        <RadialLine r1={271} r2={390} angle={angle + sign * 4.4} stroke="#a4c8d8" opacity=".7" strokeWidth=".8" />
        <circle cx={mx} cy={my} r="11" fill="#03101a" stroke="#b8dce9" strokeWidth="2" />
        <path d={`M${mx - 3} ${my} l3 -3 3 3 -3 3Z`} fill="#e8fbff" />
        <text x={tx} y={my} dy=".35em" textAnchor="middle" fontSize="15" fill="#d3e8f2">{label.toUpperCase()}</text>
      </NavLink>;
    }))}
    {CORE_NAV.bottom.map((label, i) => {
      const angle = 230 - i * 17, [x, y] = point(346, angle);
      return <NavLink key={label} label={label} onNavigate={onNavigate}>
        <path className="reactor-nav-hit" d={band(278, 370, angle - 8, angle + 8)} />
        <path d={arc(281, angle - 7.7, angle + 7.7)} fill="none" stroke="var(--reactor-ring)" opacity=".7" strokeWidth="2.5" />
        <path d={band(278, 369, angle - 8, angle + 8)} fill="none" stroke="#b6d8e6" strokeWidth=".9" opacity=".7" />
        <text x={x} y={y} dy=".35em" textAnchor="middle" transform={`rotate(${angle - 180} ${x} ${y})`} fontSize="16" fill="#c4f1ff">{label.toUpperCase()}</text>
      </NavLink>;
    })}
  </g>;
}

export function EmergencyArc({ id, emergency, onActivate }) {
  const label = emergency ? "Restore Fleet" : "Emergency Stop";
  return <g role="button" tabIndex={0} aria-label={label} data-testid="emergency-stop-btn" className="reactor-emergency" onClick={onActivate}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onActivate?.(); } }}>
    <title>{label}</title>
    <path d={band(320, 375, 122, 164)} className="reactor-emergency-hit" />
    <path d={arc(367, 123, 163)} fill="none" stroke="#ff7186" strokeWidth="1" opacity=".7" />
    <text fontSize="23" className="reactor-labels" fill="#ff667c" filter={`url(#${id}-bloom)`}>
      <textPath href={`#${id}-stop-arc`} startOffset="52%" textAnchor="middle">{label.toUpperCase()}</textPath>
    </text>
    <g transform="translate(509 726) rotate(-20)"><path d="M0 -12 L12 10 H-12 Z M0 -4 V2 M0 5 V6" fill="none" stroke="#ff7186" strokeWidth="2" /></g>
  </g>;
}

export function ReactorReadouts({ id, health, emergency, state, taskLabel }) {
  const score = emergency ? 0 : health?.core_health?.score;
  const status = emergency ? "OFFLINE" : health?.core_health?.status?.toUpperCase() || "SYNCING";
  return <g pointerEvents="none" className="reactor-labels">
    <text data-testid="core-systems-status" fill="var(--reactor-glow)" fontSize="17" letterSpacing="1">
      <textPath href={`#${id}-online-arc`} startOffset="50%" textAnchor="middle">{emergency ? "APP WORKERS HALTED" : health ? "APPLICATION HOST CONNECTED" : "AWAITING TELEMETRY"}</textPath>
    </text>
    <text x={CX} y="639" textAnchor="middle" fontSize="12" letterSpacing="3" fill="var(--reactor-glow)" data-testid="core-live-state" aria-live="polite">{state.toUpperCase()}</text>
    <text x={CX} y="677" textAnchor="middle" fill="#9af4ff" fontSize="18" data-testid="core-health-readout">CORE <tspan data-testid="core-health-status">{status}</tspan><tspan dx="18" fill="#d9f4ff" className="font-num">{score == null ? "—" : `${score}%`}</tspan></text>
    <g data-testid="core-health-bar" role="progressbar" aria-label="Composite core health" aria-valuemin={0} aria-valuemax={100} aria-valuenow={score ?? undefined}>
      {Array.from({ length: 12 }, (_, i) => <path key={i} d={`M${308 + i * 17} 688 l12 0 -5 10 -12 0 Z`} fill={score != null && i < Math.round(score / 100 * 12) ? "var(--reactor-glow)" : "#132c3c"} />)}
    </g>
    <text x={CX} y="778" textAnchor="middle" fontSize="10" letterSpacing="1.2" fill="#7ca2b5" data-testid="core-health-source">LIVE APP HOST + WORKERS · VPS NOT CONNECTED</text>
    {state === "executing" && taskLabel && <text x={CX} y="755" textAnchor="middle" fontSize="12" fill="#b4faff" data-testid="core-exec-readout">{taskLabel.length > 48 ? `${taskLabel.slice(0, 45)}…` : taskLabel}<title>{taskLabel}</title></text>}
  </g>;
}
